import { useEffect, useState } from 'react'
import { Pagination, PaginationItem, PaginationLink } from 'reactstrap'

const PMBPagination = ({
  rowCount = 0,
  currentPage = 0,
  rowsPerPage = 10,
  setCurrentPage,
  setRowsPerPage,
  customRowsPerPageOptions = [10, 30, 50, 100, 200],
  testId = 'pmb-pagination',
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768)
  const [inputValue, setInputValue] = useState('')

  const rowsPerPageOptions = customRowsPerPageOptions

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const totalPages = Math.max(1, Math.ceil((rowCount || 0) / (rowsPerPage || 1)))
  const maxPageDisplay = 5
  const jumpAmount = 1

  // REST INPUT WHEN CURRENT PAGE CHANGE
  useEffect(() => {
    setInputValue((currentPage + 1).toString())
  }, [currentPage])

  const getPageNumbers = () => {
    if (totalPages <= maxPageDisplay) {
      return Array.from({ length: totalPages }, (_, i) => i)
    }

    if (currentPage <= 2) {
      return [0, 1, 2, 'nextJump', totalPages - 1]
    } else if (currentPage >= totalPages - 3) {
      return [0, 'prevJump', totalPages - 3, totalPages - 2, totalPages - 1]
    } else {
      return [
        0,
        'prevJump',
        currentPage - 1,
        currentPage,
        currentPage + 1,
        'nextJump',
        totalPages - 1,
      ]
    }
  }

  const handlePageInputChange = (e) => {
    const value = e.target.value
    // ALLOW NUMBER ONLY
    if (value === '' || /^[1-9]\d*$/.test(value)) {
      setInputValue(value)
    }
  }

  const handlePageInputSubmit = (e) => {
    if (e.key === 'Enter') {
      const newPage = parseInt(inputValue) - 1
      if (!isNaN(newPage)) {
        const validatedPage = Math.max(0, Math.min(totalPages - 1, newPage))
        setCurrentPage(validatedPage)
      }
    } else if (e.key === 'Escape') {
      setInputValue((currentPage + 1).toString())
    }
  }

  const handlePageInputBlur = () => {
    setInputValue((currentPage + 1).toString())
  }

  return (
    <div
      className={`d-flex ${
        isMobile ? 'flex-column align-items-center w-100' : 'align-items-center'
      } justify-content-end py-2 border-top`}
    >
      {/* COUNT OF PAGE */}
      <div className="text-sm me-2">
        {`${currentPage * rowsPerPage + 1} - ${Math.min(
          (currentPage + 1) * rowsPerPage,
          rowCount,
        )} of ${rowCount}`}
      </div>

      {/* DROPDOWN TOTAL ROW PER PAGE */}
      <div className="d-flex align-items-center m-2">
        <span className="text-sm me-2" style={{ whiteSpace: 'nowrap' }}>
          Baris per halaman:
        </span>
        <select
          size="xs"
          className="form-select form-select-md"
          value={rowsPerPage}
          data-testid={`${testId}-rows-per-page`}
          onChange={(e) => {
            setRowsPerPage(Number(e.target.value))
            setCurrentPage(0, e?.target?.value)
          }}
        >
          {rowsPerPageOptions.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </div>

      {/* NEW PAGINATION */}
      <div
        className={`d-flex ${isMobile ? 'w-100 justify-content-center mt-2' : 'align-items-center'}`}
      >
        <Pagination size="md" className="mt-3">
          {/* BUTTON LAST FIRST */}
          <PaginationItem disabled={currentPage === 0}>
            <PaginationLink
              first
              onClick={() => setCurrentPage(0)}
              data-testid={`${testId}-first`}
            />
          </PaginationItem>

          {/* BUTTON PREV */}
          <PaginationItem disabled={currentPage === 0}>
            <PaginationLink
              previous
              onClick={() => setCurrentPage(currentPage - 1)}
              data-testid={`${testId}-prev`}
            />
          </PaginationItem>

          {/* PAGE NUMBER */}
          {getPageNumbers().map((page, index) =>
            page === 'prevJump' ? (
              <PaginationItem key={index}>
                <PaginationLink
                  data-testid={`${testId}-jump-prev`}
                  onClick={() =>
                    setCurrentPage(Math.max(0, currentPage - jumpAmount))
                  }
                >
                  ...
                </PaginationLink>
              </PaginationItem>
            ) : page === 'nextJump' ? (
              <PaginationItem key={index}>
                <PaginationLink
                  data-testid={`${testId}-jump-next`}
                  onClick={() =>
                    setCurrentPage(
                      Math.min(totalPages - 1, currentPage + jumpAmount),
                    )
                  }
                >
                  ...
                </PaginationLink>
              </PaginationItem>
            ) : (
              <PaginationItem key={page} active={page === currentPage}>
                {page === currentPage && totalPages > 1 ? (
                  <div
                    style={{
                      padding: '2px',
                      borderRadius: '6px',
                      background: 'linear-gradient(to right, #006386, #17a2b8)',
                      display: 'inline-block',
                      marginRight: '1px',
                    }}
                  >
                    <input
                      type="text"
                      value={inputValue}
                      data-testid={`${testId}-page-input`}
                      onChange={handlePageInputChange}
                      onKeyDown={handlePageInputSubmit}
                      onBlur={handlePageInputBlur}
                      style={{
                        width: '32px',
                        height: '34px',
                        textAlign: 'center',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '16px',
                        outline: 'none',
                        backgroundColor: 'white',
                        display: 'block',
                      }}
                    />
                  </div>
                ) : (
                  <PaginationLink
                    onClick={() => setCurrentPage(page)}
                    data-testid={`${testId}-page`}
                  >
                    {page + 1}
                  </PaginationLink>
                )}
              </PaginationItem>
            ),
          )}

          {/* BUTTON NEXT */}
          <PaginationItem disabled={currentPage === totalPages - 1}>
            <PaginationLink
              next
              onClick={() => setCurrentPage(currentPage + 1)}
              data-testid={`${testId}-next`}
            />
          </PaginationItem>

          {/* BUTTON NEXT LAST */}
          <PaginationItem disabled={currentPage === totalPages - 1}>
            <PaginationLink
              last
              onClick={() => setCurrentPage(totalPages - 1)}
              data-testid={`${testId}-last`}
            />
          </PaginationItem>
        </Pagination>
      </div>
    </div>
  )
}

export default PMBPagination
